import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './css/LoginPanel.css';
import { useAuth } from '../admin/AuthContext';
import { useLoading } from '../contexts/LoadingContext';

const LoginPanel = ({ onClose, onRequestLogout, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { user, login, loading } = useAuth();
  const { startLoading, stopLoading, updateMessage } = useLoading();

  console.log('LoginPanel rendered, user:', user);

  const handleLogin = async () => {
    console.log('LoginPanel: handleLogin called.');
    
    // 유효성 검사
    if (!email.trim()) {
      setLoginError('이메일을 입력해주세요.');
      return;
    }
    
    if (!password.trim()) {
      setLoginError('비밀번호를 입력해주세요.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLoginError('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setLoginError('');
    startLoading('로그인 중...');

    try {
      const result = await login({
        email: email.trim(),
        password: password.trim()
      });

      if (result.success) {
        console.log('로그인 성공:', result.user);
        
        // 비밀번호 저장 옵션 처리
        if (remember) {
          localStorage.setItem('rememberEmail', email);
        } else {
          localStorage.removeItem('rememberEmail');
        }
        
        // 패널 닫기
        onClose();
      } else {
        setLoginError(result.error || '로그인에 실패했습니다.');
      }
    } catch (error) {
      console.error('로그인 처리 오류:', error);
      setLoginError('로그인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
      stopLoading();
    }
  };

  // 로그아웃 메뉴 클릭 시: 사이드바만 닫고, 상위(MainLayout)에서 로그아웃 확인 모달 표시
  const handleLogout = (e) => {
    if (e) e.preventDefault();
    onRequestLogout?.();
  };

  const handleClose = () => {
    console.log('LoginPanel: handleClose called.');
    setMode('login'); // Reset to login mode when closing
    onClose();
  };

  const handleLinkClick = (path) => {
    console.log(`LoginPanel: handleLinkClick called with path: ${path}`);
    navigate(path);
    onClose();
  };

  // 컴포넌트 마운트 시 저장된 이메일 불러오기
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
  }, []);

  // 엔터 키 처리
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin();
    }
  };

  const getTitle = () => {
    if (user) return '마이 페이지';
    return mode === 'register' ? '회원가입' : '로그인';
  };

  const handleRegisterClick = () => {
    setMode('register');
  };

  const handleRegisterNext = () => {
    // 회원가입 페이지로 이동
    window.open('https://www.busbarworld.com/signup', '_blank');
    onClose();
  };

  const renderLogin = () => (
    <div className="login-form-wrapper" key="login-form">
      <div className="login-form-container">
        {loginError && (
          <div className="login-error-message" style={{ 
            color: '#ff4444', 
            fontSize: '14px', 
            marginBottom: '10px', 
            textAlign: 'center' 
          }}>
            {loginError}
          </div>
        )}
        <div className="form-group">
            <label htmlFor="login-email">이메일 주소 *</label>
            <input 
                id="login-email" 
                type="email" 
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setLoginError(''); // 에러 메시지 클리어
                }}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                placeholder=""
            />
        </div>
        <div className="form-group">
            <label htmlFor="login-password">비밀번호 *</label>
            <input 
                id="login-password" 
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setLoginError(''); // 에러 메시지 클리어
                }}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                placeholder=""
            />
        </div>
        <div className="login-options">
            <label className="remember-me">
                <input 
                  type="checkbox" 
                  checked={remember} 
                  onChange={() => setRemember(!remember)}
                  disabled={isLoading}
                />
                비밀번호 저장
            </label>
            <a 
                href="https://www.busbarworld.com/find-credentials" 
                target="_blank" 
                rel="noopener noreferrer"
                className="find-credentials"
            >
                아이디 / 비밀번호 찾기
            </a>
        </div>
      </div>
      <div className="login-actions">
          <button 
            className="btn-login" 
            onClick={handleLogin}
            disabled={isLoading}
          >
            <div>{isLoading ? '로그인 중...' : '로그인'}</div>
          </button>
          <button 
            className="btn-new-registration" 
            onClick={handleRegisterClick}
            disabled={isLoading}
          >
            <div>회원가입</div>
          </button>
      </div>
    </div>
  );

  const renderRegister = () => (
    <div className="register-form-wrapper" key="register-form">
      <div className="register-form-container">
        <div className="form-group">
            <label htmlFor="register-email">등록하려는 이메일 주소 *</label>
            <input 
                id="register-email" 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                placeholder="예 : busbar@busbarworld.com"
            />
        </div>
        <div className="register-actions">
          <button 
            className="btn-register-next" 
            onClick={handleRegisterNext}
            disabled={isLoading}
          >
            <div>다음</div>
          </button>
        </div>
        <div className="register-info-sections">
          <div className="info-section">
            <div className="info-section-header">
              <h3>등록 및 상품 견적 전의 주의점</h3>
              <div className="info-divider"></div>
            </div>
            <div className="info-section-content">
              당사에서 구입한 상품은 일반 공업 용도로만 사용할 수 있습니다.
              <br />
              <br />
              일반 공업용도 이외의 용도로는 사용할 수 없습니다.
              <br />
              <br />
              상품을 수출할 고객님은 이 상품을 수출하거나 재수출할 경우을 확인해 주세요
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <h3>이용 약관</h3>
              <div className="info-divider"></div>
            </div>
            <div className="info-section-content">
              당사와의 거래에는 부스바월드 카탈로그 이용 약관 및 기타 회사의 규약이 적용됩니다.
              <br />
              <br />
              고객님의 개인정보는 개인 정보 · 고객 정보 보호 방침에 따라 처리됩니다.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  const renderLoggedIn = () => (
    <div className="logged-in-container" key="logged-in">
      <div className="user-welcome">
        <p><strong>{user.name}</strong> 님</p>
        <p>고객코드 {user.id.toString().padStart(5, '0')}</p>
      </div>
      <div className="my-page-menu">
        <a href="#" onClick={() => handleLinkClick('/my-page')} className="menu-item">My 페이지</a>
        <a href="#" onClick={() => handleLinkClick('/quotation-history')} className="menu-item">견적이력</a>
        <a href="#" onClick={() => handleLinkClick('/custom-order')} className="menu-item">주문제작</a>
        <a href="#" onClick={() => handleLinkClick('/change-password')} className="menu-item">비밀번호 변경</a>
        <a href="#" onClick={() => handleLinkClick('/withdrawal')} className="menu-item">회원탈퇴</a>
        <a href="#" className="menu-item" onClick={(e) => { e.preventDefault(); handleLogout(e); }}>로그아웃</a>
      </div>
    </div>
  );

  // 로딩 중일 때 표시
  if (loading) {
    return (
      <div className="login-panel-container" key="loading">
        <div className="login-panel-overlay open" onClick={handleClose}></div>
        <div className="login-panel open">
          <div className="login-panel-header">
            <div className="login-title-section">
              <span className="login-icon">👤</span>
              <h2>로딩 중...</h2>
            </div>
            <button onClick={handleClose} className="close-button">&times;</button>
          </div>
          <div className="login-panel-body" style={{ textAlign: 'center', padding: '20px' }}>
            <div>사용자 정보를 확인하고 있습니다...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-panel-container" key="main">
      <div className="login-panel-overlay open" onClick={handleClose}></div>
      <div className="login-panel open" onClick={(e) => {
          console.log('Clicked inside login-panel, stopping propagation.');
          e.stopPropagation();
        }}>
        <div className="login-panel-header">
          <div className="offcanvas-tit">
            <div className="login-title-section">
              <img className="login-icon" src="/icon0.svg" alt="icon" onError={(e) => { e.target.style.display = 'none'; }} />
              <h2>{getTitle()}</h2>
            </div>
            <div className="header-divider"></div>
          </div>
        </div>
        <div className="login-panel-body">
            {user ? renderLoggedIn() : (mode === 'register' ? renderRegister() : renderLogin())}
        </div>
        <button onClick={handleClose} className="close-button">
          <div className="close-button-circle">
            <img src="/close0.svg" alt="close" onError={(e) => { 
              if (e.target.parentElement) {
                e.target.parentElement.innerHTML = '×';
                e.target.parentElement.style.fontSize = '24px';
                e.target.parentElement.style.color = '#1C1B1F';
              }
            }} />
          </div>
        </button>
      </div>
    </div>
  );
};

export default LoginPanel; 